/**
 * CinematicDirector — State machine that orchestrates cinematic sequences.
 * Processes steps sequentially, delegating to sub-components:
 *   - CameraDirector for pans/zooms
 *   - BubbleRenderer for dialogue bubbles
 *   - FakeCursor for animated pointer
 *   - RecapPanel for post-wave recap
 *   - DemoWaveRunner for scripted game actions
 *
 * Triggered via EventBus: emit(CinematicEvents.START, { sequenceId })
 * Persists completion in cinematic-state.json (not localStorage).
 */
import Phaser from 'phaser';
import { EventBus, GameEvents } from '@/events/EventBus';
import { CinematicEvents } from './CinematicEvents';
import { CameraDirector, findFirstPlacementTile } from './CameraDirector';
import { BubbleRenderer } from './BubbleRenderer';
import { FakeCursor } from './FakeCursor';
import { RecapPanel } from './RecapPanel';
import { SpotlightRenderer } from './SpotlightRenderer';
import { ConditionRegistry } from './ConditionRegistry';
import { DemoWaveRunner } from './DemoWaveRunner';
import { CANVAS_WIDTH, CANVAS_HEIGHT } from '@/config/GameConfig';
import { GOD } from '@/config/GodMode';
import { createSmallTechButton } from '@/ui/TechUI';
import { t, addTranslations } from '@/i18n/I18n';
import { isCinematicCompleted, markCinematicCompleted, resetCinematicCompletion } from './CinematicState';
import type {
  CinematicSequence, CinematicStep,
  DialogueStep, CameraPanStep, FakeClickStep,
  ActionStep, WaitStep, RecapStep, SpotlightStep,
} from './CinematicTypes';

// Registry of all sequences — populated by importing sequence files + cinematics.json
const SEQUENCES = new Map<string, CinematicSequence>();
/** IDs that have editor-saved versions (from JSON file) — these take priority over hardcoded */
const PERSISTED_IDS = new Set<string>();

let _syncTimer: ReturnType<typeof setTimeout> | null = null;

/** Persist editor-saved sequences to cinematics.json via Vite dev endpoint */
function persistSequences(): void {
  try {
    const obj: Record<string, CinematicSequence> = {};
    for (const id of PERSISTED_IDS) {
      const seq = SEQUENCES.get(id);
      if (seq) obj[id] = seq;
    }
    const json = JSON.stringify(obj, null, 2);
    // Debounce: avoid rapid writes during editing
    if (_syncTimer) clearTimeout(_syncTimer);
    _syncTimer = setTimeout(() => {
      fetch('/api/save-cinematics', { method: 'POST', body: json })
        .then(r => { if (!r.ok) console.warn('[CinematicSync] save failed:', r.status); })
        .catch(() => { /* dev server not running — ignore */ });
    }, 300);
  } catch (e) { console.warn('[CinematicDirector] sync failed:', e); }
}

/** Load editor-saved sequences from cinematics.json (called at boot) */
export async function loadCinematicsFromJson(): Promise<void> {
  try {
    const resp = await fetch('/cinematics.json');
    if (!resp.ok) return; // file doesn't exist yet — fine
    const map: Record<string, CinematicSequence> = await resp.json();
    for (const [id, seq] of Object.entries(map)) {
      SEQUENCES.set(id, seq);
      PERSISTED_IDS.add(id);
      if (seq.customTranslations) {
        addTranslations('en', seq.customTranslations);
        addTranslations('fr', seq.customTranslations);
      }
    }
  } catch (e) { console.warn('[CinematicDirector] loadCinematicsFromJson failed:', e); }
}

/**
 * Register a cinematic sequence.
 * @param persist true = editor save (persisted to localStorage), false = hardcoded definition
 * Hardcoded definitions do NOT overwrite editor-saved versions.
 */
export function registerSequence(seq: CinematicSequence, persist = true): void {
  if (!persist && PERSISTED_IDS.has(seq.id)) return; // editor version takes priority
  SEQUENCES.set(seq.id, seq);
  // Register any custom translations bundled with the sequence
  if (seq.customTranslations) {
    addTranslations('en', seq.customTranslations);
    addTranslations('fr', seq.customTranslations);
  }
  if (persist) {
    PERSISTED_IDS.add(seq.id);
    persistSequences();
  }
}

/** Get a registered sequence by ID (used by Cinematic Editor to load existing) */
export function getSequence(id: string): CinematicSequence | undefined {
  return SEQUENCES.get(id);
}

/** Get all registered sequence IDs */
export function getSequenceIds(): string[] {
  return Array.from(SEQUENCES.keys());
}

/** Remove a sequence from the registry */
export function deleteSequence(id: string): boolean {
  const result = SEQUENCES.delete(id);
  if (result) persistSequences();
  return result;
}

type DirectorState = 'idle' | 'running' | 'waiting' | 'complete';

export class CinematicDirector {
  private hostScene: Phaser.Scene;
  private overlayScene: Phaser.Scene;

  // Sub-components
  private camera: CameraDirector;
  private bubble: BubbleRenderer;
  private cursor: FakeCursor;
  private recap: RecapPanel;
  private spotlight: SpotlightRenderer;
  private demoRunner: DemoWaveRunner | null;

  // State
  private state: DirectorState = 'idle';
  private sequence: CinematicSequence | null = null;
  private stepIndex = 0;
  private pendingCleanup: (() => void)[] = [];
  private skipBtn: Phaser.GameObjects.Container | null = null;
  private borderGfx: Phaser.GameObjects.Graphics | null = null;
  private borderTween: Phaser.Tweens.Tween | null = null;
  /** When true, cinematic is fast-forwarding (skip pressed) */
  private skipping = false;
  private savedSpeedMult = 1;

  constructor(hostScene: Phaser.Scene, overlayScene?: Phaser.Scene) {
    this.hostScene = hostScene;
    this.overlayScene = overlayScene ?? hostScene;

    // Safety: if cinematicSpeedMult was left at skip value (100) from a prior
    // session (page refresh during skip), clamp it back to max slider value.
    if ((GOD.cinematicSpeedMult ?? 1) > 4) {
      GOD.cinematicSpeedMult = 1;
    }

    this.camera = new CameraDirector(hostScene);
    this.bubble = new BubbleRenderer(this.overlayScene);
    this.cursor = new FakeCursor(this.overlayScene);
    this.recap = new RecapPanel(this.overlayScene);
    this.spotlight = new SpotlightRenderer(this.overlayScene);

    // DemoWaveRunner only works on GameScene (requires waveManager/robotManager)
    const wm = hostScene.registry.get('waveManager');
    this.demoRunner = wm ? new DemoWaveRunner(hostScene) : null;

    // Listen for start trigger
    EventBus.on(CinematicEvents.START, this.onStartRequested, this);
    EventBus.on(CinematicEvents.SKIP, this.skip, this);
    EventBus.on(CinematicEvents.FORCE_STOP, this.forceStop, this);
  }

  // ── Public API ──

  static shouldShow(sequenceId: string): boolean {
    if (GOD.forceCinematic) return true;
    return !isCinematicCompleted(sequenceId);
  }

  // ── Event handlers ──

  private onStartRequested(data: { sequenceId: string }): void {
    this.start(data.sequenceId);
  }

  private start(sequenceId: string): void {
    const seq = SEQUENCES.get(sequenceId);
    if (!seq) {
      console.warn(`[CinematicDirector] Unknown sequence: ${sequenceId}`);
      return;
    }

    // Clean up any stale running state before starting
    if (this.state === 'running' || this.state === 'waiting') {
      this.cleanupAll();
    }

    this.sequence = seq;
    this.stepIndex = 0;
    this.state = 'running';

    // Lock camera & input
    if (seq.lockCamera) this.hostScene.registry.set('tutorialCameraLock', true);
    if (seq.lockInput) this.hostScene.registry.set('cinematicInputLock', true);
    this.hostScene.registry.set('cinematicActive', true);

    // Show cinematic border + skip button
    this.createCinematicBorder();
    this.createSkipButton();

    // Start processing
    this.processStep();
  }

  // ── Step processing ──

  private processStep(): void {
    if (!this.sequence || this.stepIndex >= this.sequence.steps.length) {
      this.complete();
      return;
    }

    const step = this.sequence.steps[this.stepIndex];

    // ── disabled steps — skip during playback ──
    if (step.disabled) {
      this.stepIndex++;
      this.processStep();
      return;
    }

    // ── skipIf — condition-based step skip ──
    if (step.skipIf && ConditionRegistry.evaluate(step.skipIf)) {
      this.stepIndex++;
      this.processStep();
      return;
    }

    const speedMult = GOD.cinematicSpeedMult ?? 1;

    EventBus.emit(CinematicEvents.STEP_STARTED, {
      stepIndex: this.stepIndex,
      stepType: step.type,
    });

    // Handle delayBefore (skip in fast-forward mode)
    const delayBefore = this.skipping ? 0 : (step.delayBefore ? step.delayBefore / speedMult : 0);
    if (delayBefore > 0) {
      this.overlayScene.time.delayedCall(delayBefore, () => this.executeStep(step));
    } else {
      this.executeStep(step);
    }
  }

  private executeStep(step: CinematicStep): void {
    // Hide fake cursor from previous fakeClick step (fakeClick will re-show it)
    if (step.type !== 'fakeClick') this.cursor.hide();

    switch (step.type) {
      case 'dialogue':   this.handleDialogue(step);   break;
      case 'cameraPan':  this.handleCameraPan(step);   break;
      case 'fakeClick':  this.handleFakeClick(step);   break;
      case 'action':     this.handleAction(step);      break;
      case 'wait':       this.handleWait(step);        break;
      case 'recap':      this.handleRecap(step);       break;
      case 'spotlight':  this.handleSpotlight(step);   break;
    }

    // Fast-forward: auto-advance user-interaction steps after a micro-delay
    if (this.skipping) {
      this.overlayScene.time.delayedCall(1, () => this.forceAdvanceCurrentStep());
    }
  }

  private handleDialogue(step: DialogueStep): void {
    // ── Optional spotlight highlight alongside dialogue ──
    const hasHighlight = !!step.highlightTarget;
    if (hasHighlight) {
      const target = SpotlightRenderer.resolveWorldRects(
        step.highlightTarget!, step.highlightScreenSpace, this.hostScene,
      );
      if (target && target.rects.length > 0) {
        this.spotlight.showWorld(target.rects, this.hostScene, target.isScreen, {
          dimAlpha: step.highlightDimAlpha,
          highlightColor: step.highlightColor,
          pulse: step.highlightPulse,
          restrictInput: false, // dialogue handles input, not spotlight
        });
      }
    }

    this.bubble.show({
      speaker: step.speaker,
      textKey: step.textKey,
      position: step.position,
      emotion: step.emotion,
      autoDismissMs: step.autoDismissMs,
      worldAnchor: step.worldAnchor,
      portrait: step.portrait,
      portraitDisplay: step.portraitDisplay,
      portraitScale: step.portraitScale,
      portraitFrame: step.portraitFrame,
      portraitOffset: step.portraitOffset,
      portraitTransition: step.portraitTransition,
      portraitFlip: step.portraitFlip,
      frameScale: step.frameScale,
      frameOffset: step.frameOffset,
      bubbleTemplate: step.bubbleTemplate,
      bubblePos: step.bubblePos,
      tailTip: step.tailTip,
      borderColor: step.borderColor,
      fillColor: step.fillColor,
      textColor: step.textColor,
    });

    const unsub = () => EventBus.off(CinematicEvents.BUBBLE_DISMISSED, onDone);
    const onDone = () => {
      unsub();
      if (hasHighlight) this.spotlight.hide();
      this.onStepDone(step);
    };
    EventBus.once(CinematicEvents.BUBBLE_DISMISSED, onDone);
    this.pendingCleanup.push(unsub);
  }

  private handleCameraPan(step: CameraPanStep): void {
    this.camera.panTo(step.target, step.zoom, step.duration);

    const unsub = () => EventBus.off(CinematicEvents.CAMERA_PAN_DONE, onDone);
    const onDone = () => { unsub(); this.onStepDone(step); };
    EventBus.once(CinematicEvents.CAMERA_PAN_DONE, onDone);
    this.pendingCleanup.push(unsub);
  }

  private handleFakeClick(step: FakeClickStep): void {
    let target: { x: number; y: number };

    // Helper: convert world coords to screen coords using host camera
    const worldToScreen = (wx: number, wy: number) => {
      const cam = this.hostScene.cameras.main;
      const halfW = cam.width / 2;
      const halfH = cam.height / 2;
      return {
        x: (wx - cam.scrollX - halfW) * cam.zoom + halfW,
        y: (wy - cam.scrollY - halfH) * cam.zoom + halfH,
      };
    };

    if (typeof step.target === 'object') {
      // Custom coords — convert world→screen if not in screen space
      target = step.screenSpace ? step.target : worldToScreen(step.target.x, step.target.y);
    } else if (step.target === 'placementTile') {
      const tile = findFirstPlacementTile();
      if (tile) {
        target = step.screenSpace ? tile : worldToScreen(tile.x, tile.y);
      } else {
        target = { x: CANVAS_WIDTH / 2, y: CANVAS_HEIGHT / 2 };
      }
    } else if (step.target === 'startButton') {
      // Approximate position of the start button in UIScene (screen space)
      target = { x: CANVAS_WIDTH / 2, y: CANVAS_HEIGHT - 40 };
    } else {
      target = { x: CANVAS_WIDTH / 2, y: CANVAS_HEIGHT / 2 };
    }

    const unsub = () => EventBus.off(CinematicEvents.FAKE_CLICK_DONE, onDone);
    const onDone = () => {
      unsub();
      // Dispatch real pointer event if requested
      if (step.dispatch) {
        this.dispatchRealClick(target);
      }
      this.onStepDone(step);
    };
    EventBus.once(CinematicEvents.FAKE_CLICK_DONE, onDone);
    this.pendingCleanup.push(unsub);

    this.cursor.moveAndClick(target, undefined, step.holdMs);
  }

  /** Dispatch a real pointer event at the given screen coords using hitTest.
   *  Searches ALL active scenes (UIScene, overlayScene, hostScene) so that
   *  screen-space UI buttons are found, not just hostScene game objects.
   *  Temporarily lifts cinematicActive lock so UI handlers don't block. */
  private dispatchRealClick(target: { x: number; y: number }): void {
    const manager = this.hostScene.input.manager;
    if (!manager) return;
    const pointer = this.hostScene.input.activePointer;
    const origX = pointer.x, origY = pointer.y;
    pointer.x = target.x;
    pointer.y = target.y;
    pointer.worldX = target.x;
    pointer.worldY = target.y;

    // Set dispatching flag — UI guards check this to let the click through
    this.hostScene.registry.set('cinematicDispatching', true);

    // Collect all active scenes — try overlay, UI, and host (top-to-bottom order)
    const scenesToTest: Phaser.Scene[] = [];
    if (this.overlayScene !== this.hostScene) scenesToTest.push(this.overlayScene);
    const uiScene = this.hostScene.scene.get('UIScene');
    if (uiScene && this.hostScene.scene.isActive('UIScene')) scenesToTest.push(uiScene);
    scenesToTest.push(this.hostScene);

    let dispatched = false;
    for (const scene of scenesToTest) {
      const cam = scene.cameras.main;
      // Use input._list which includes interactive objects inside containers
      // (displayList.getChildren() only has top-level objects)
      const interactiveList = (scene.input as any)._list as Phaser.GameObjects.GameObject[] ?? [];
      const hitObjects = interactiveList.length > 0
        ? manager.hitTest(pointer, interactiveList, cam)
        : [];
      if (hitObjects.length > 0) {
        for (const obj of hitObjects) {
          if (obj.input?.enabled) {
            obj.emit('pointerdown', pointer, obj.input.localX, obj.input.localY, {} as Phaser.Types.Input.EventData);
            obj.emit('pointerup', pointer, obj.input.localX, obj.input.localY, {} as Phaser.Types.Input.EventData);
            dispatched = true;
            break;
          }
        }
      }
      if (dispatched) break;
    }

    // If no interactive object was hit, emit on scene-level input handlers
    // (e.g. UIScene's map click handler which listens on this.input.on('pointerdown'))
    if (!dispatched) {
      for (const scene of scenesToTest) {
        scene.input.emit('pointerdown', pointer);
        scene.input.emit('pointerup', pointer);
      }
    }

    // Clear dispatching flag
    this.hostScene.registry.set('cinematicDispatching', false);

    pointer.x = origX;
    pointer.y = origY;
  }

  private handleAction(step: ActionStep): void {
    if (this.demoRunner) {
      this.demoRunner.execute(step.action, (step.params ?? {}) as Record<string, unknown>);
    } else {
      console.warn(`[CinematicDirector] Action '${step.action}' skipped — no game managers on ${this.hostScene.scene.key}`);
    }
    // Actions complete immediately
    this.onStepDone(step);
  }

  private handleWait(step: WaitStep): void {
    this.state = 'waiting';
    const speedMult = GOD.cinematicSpeedMult ?? 1;

    let resolved = false;

    // ── Combat simulation mode ──
    if (step.simulateCombat) {
      this.hostScene.registry.set('cinematicSimulation', true);
      // Start the wave via GameScene so all combat systems activate
      const gs = this.hostScene as any;
      if (gs.startWave) gs.startWave();

      const cleanup = () => {
        this.hostScene.registry.set('cinematicSimulation', false);
        EventBus.off(GameEvents.WAVE_COMPLETED, onCombatEnd);
        EventBus.off(GameEvents.GAME_OVER, onCombatEnd);
      };
      const onCombatEnd = () => {
        if (resolved) return;
        resolved = true;
        cleanup();
        this.state = 'running';
        this.onStepDone(step);
      };
      EventBus.on(GameEvents.WAVE_COMPLETED, onCombatEnd, this);
      EventBus.on(GameEvents.GAME_OVER, onCombatEnd, this);
      this.pendingCleanup.push(cleanup);
      // No timeout — combat runs until one side wins
      return;
    }

    // ── Normal wait mode ──
    const resolve = () => {
      if (resolved) return;
      resolved = true;
      this.state = 'running';
      this.onStepDone(step);
    };

    // Fast-forward: resolve waits immediately
    if (this.skipping) {
      resolve();
      return;
    }

    if (step.waitEvent) {
      const unsub = () => EventBus.off(step.waitEvent!, resolve);
      EventBus.once(step.waitEvent, resolve);
      this.pendingCleanup.push(unsub);
    }

    if (step.timeoutMs) {
      this.overlayScene.time.delayedCall(step.timeoutMs / speedMult, resolve);
    }

    if (!step.waitEvent && !step.timeoutMs) {
      resolve();
    }
  }

  private handleRecap(step: RecapStep): void {
    this.cursor.hide();
    this.recap.show({
      titleKey: step.titleKey,
      items: step.items,
      confirmKey: step.confirmKey,
    });

    const unsub = () => EventBus.off(CinematicEvents.RECAP_CLOSED, onDone);
    const onDone = () => { unsub(); this.onStepDone(step); };
    EventBus.once(CinematicEvents.RECAP_CLOSED, onDone);
    this.pendingCleanup.push(unsub);
  }

  private handleSpotlight(step: SpotlightStep): void {
    const target = SpotlightRenderer.resolveWorldRects(step.target, step.screenSpace, this.hostScene);
    if (!target || target.rects.length === 0) {
      console.warn(`[CinematicDirector] Spotlight target unresolved, skipping`);
      this.onStepDone(step);
      return;
    }

    this.spotlight.showWorld(target.rects, this.hostScene, target.isScreen, {
      dimAlpha: step.dimAlpha,
      highlightColor: step.highlightColor,
      pulse: step.pulse,
      labelKey: step.labelKey,
      labelPosition: step.labelPosition,
      arrowDir: step.arrowDir,
      restrictInput: step.restrictInput,
    });

    const speedMult = GOD.cinematicSpeedMult ?? 1;
    let resolved = false;
    const onDone = () => {
      if (resolved) return;
      resolved = true;
      this.spotlight.hide();
      this.onStepDone(step);
    };

    if (step.advanceOn === 'event' && step.waitEvent) {
      const unsub = () => EventBus.off(step.waitEvent!, onDone);
      EventBus.once(step.waitEvent, onDone);
      this.pendingCleanup.push(unsub);
    } else {
      // Default: advance on spotlight click
      const unsub = () => EventBus.off(CinematicEvents.SPOTLIGHT_CLICKED, onDone);
      EventBus.once(CinematicEvents.SPOTLIGHT_CLICKED, onDone);
      this.pendingCleanup.push(unsub);
    }

    if (step.timeoutMs) {
      this.overlayScene.time.delayedCall(step.timeoutMs / speedMult, onDone);
    }
  }

  // ── Step advancement ──

  private onStepDone(step: CinematicStep): void {
    const speedMult = GOD.cinematicSpeedMult ?? 1;
    const delayAfter = this.skipping ? 0 : (step.delayAfter ? step.delayAfter / speedMult : 0);

    EventBus.emit(CinematicEvents.STEP_DONE);
    this.stepIndex++;

    if (delayAfter > 0) {
      this.overlayScene.time.delayedCall(delayAfter, () => this.processStep());
    } else {
      this.processStep();
    }
  }

  // ── Completion & cleanup ──

  private complete(): void {
    this.state = 'complete';

    // Restore speed mult if we were fast-forwarding
    if (this.skipping) {
      GOD.cinematicSpeedMult = this.savedSpeedMult;
      this.skipping = false;
    }

    // Hide fake cursor (it may still be visible from a fakeClick step)
    this.cursor.hide();

    // Unlock camera & input
    if (this.sequence?.lockCamera) this.hostScene.registry.set('tutorialCameraLock', false);
    if (this.sequence?.lockInput) this.hostScene.registry.set('cinematicInputLock', false);
    this.hostScene.registry.set('cinematicActive', false);
    this.hostScene.registry.set('cinematicSimulation', false);

    // Restore GameScene to WORKSHOP phase so robots can be selected/managed
    const gs = this.hostScene as any;
    if (gs.phase != null && gs.phase !== 'workshop') {
      gs.phase = 'workshop';
      EventBus.emit(GameEvents.PHASE_CHANGED, 'workshop');
    }

    // Persist completion (skip preview sequences — EditorPreview manages their lifecycle)
    if (this.sequence) {
      const isPreview = this.sequence.id === '__preview__';

      // Reset camera — but NOT for preview sequences: the editor manages camera
      // itself, and a 600ms cam.pan() here creates a persistent panEffect that
      // fights with the editor's direct camera manipulation (bounce-back).
      if (!isPreview) {
        this.camera.reset();
      } else {
        // Just kill any active effects so they don't linger on the paused scene
        const cam = this.hostScene.cameras.main;
        (cam as any).panEffect?.reset();
        (cam as any).zoomEffect?.reset();
      }

      if (!isPreview) {
        markCinematicCompleted(this.sequence.id);
        if (GOD.forceCinematic) GOD.forceCinematic = false;
      }
      EventBus.emit(CinematicEvents.COMPLETE, { sequenceId: this.sequence.id });
    }

    this.cleanupAll();
  }

  skip(): void {
    // Skip = immediate stop (destroy all cinematic artifacts)
    this.forceStop();
  }

  /** Immediately halt the cinematic — destroy all artifacts and mark as completed. */
  forceStop(): void {
    if (this.state === 'idle' && !this.sequence) return;
    // Restore speed if skip was in progress
    if (this.skipping) {
      GOD.cinematicSpeedMult = this.savedSpeedMult;
    }
    this.skipping = false;
    this.state = 'idle';
    this.stepIndex = 0;
    // Dismiss all active elements immediately (silent=true to avoid tweens
    // that would freeze on paused scenes and leak into normal game mode)
    this.bubble.dismiss(true);
    this.recap.hide();
    this.cursor.hide();
    this.spotlight.hide();
    // Unlock camera & input
    this.hostScene.registry.set('tutorialCameraLock', false);
    this.hostScene.registry.set('cinematicInputLock', false);
    this.hostScene.registry.set('cinematicActive', false);
    // Persist completion + emit COMPLETE so downstream systems know it's done
    if (this.sequence) {
      const isPreview = this.sequence.id === '__preview__';
      if (!isPreview) {
        this.camera.reset();
        markCinematicCompleted(this.sequence.id);
        if (GOD.forceCinematic) GOD.forceCinematic = false;
      }
      EventBus.emit(CinematicEvents.COMPLETE, { sequenceId: this.sequence.id });
    }
    this.cleanupAll();
  }

  /** Force-advance any step that's blocked on user interaction (dialogue click, recap confirm, spotlight click) */
  private forceAdvanceCurrentStep(): void {
    // Dismiss active dialogue bubble → triggers BUBBLE_DISMISSED → onStepDone
    this.bubble.dismiss();
    // Close active recap panel → triggers RECAP_CLOSED → onStepDone
    this.recap.hide();
    // Click spotlight → triggers SPOTLIGHT_CLICKED → onStepDone
    EventBus.emit(CinematicEvents.SPOTLIGHT_CLICKED);
    // Hide cursor (fakeClick completes via its own timer, already sped up)
    this.cursor.hide();
  }

  private cleanupAll(): void {
    // Destroy demo-spawned enemies so waveActive resets and normal waves can start
    this.demoRunner?.destroyAllDemo();

    // Run pending unsub callbacks (try-catch to ensure all run even if one throws)
    for (const fn of this.pendingCleanup) {
      try { fn(); } catch (e) { console.warn('[CinematicDirector] cleanup error:', e); }
    }
    this.pendingCleanup = [];
    this.skipBtn?.destroy();
    this.skipBtn = null;
    if (this.borderTween) { this.borderTween.destroy(); this.borderTween = null; }
    if (this.borderGfx) { this.borderGfx.destroy(); this.borderGfx = null; }
    // Kill any stray tweens on cinematic objects (prevents frozen tweens leaking into game)
    this.bubble.killTweens();
    this.sequence = null;
  }

  private createSkipButton(): void {
    this.skipBtn = createSmallTechButton(
      this.overlayScene,
      CANVAS_WIDTH - 70, CANVAS_HEIGHT - 25,
      t('cinematic.skip'),
      0x666688,
      () => this.skip(),
      580,
    );
  }

  /** Purple pulsing border around the screen during cinematic playback */
  private createCinematicBorder(): void {
    if (this.borderGfx) { this.borderGfx.destroy(); this.borderGfx = null; }
    if (this.borderTween) { this.borderTween.destroy(); this.borderTween = null; }

    const gfx = this.overlayScene.add.graphics().setDepth(570);
    const color = 0x9944ff;
    const w = CANVAS_WIDTH;
    const h = CANVAS_HEIGHT;

    // Draw multiple inset layers for a soft glow effect
    const layers = 8;
    for (let i = 0; i < layers; i++) {
      const alpha = 0.6 - i * 0.06;
      const lw = 4 - i * 0.4;
      gfx.lineStyle(lw, color, alpha);
      gfx.strokeRect(i * 3, i * 3, w - i * 6, h - i * 6);
    }

    this.borderGfx = gfx;
    this.borderTween = this.overlayScene.tweens.add({
      targets: gfx,
      alpha: { from: 1, to: 0.3 },
      duration: 1200,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  /** Reset a cinematic's completion state (for dev/testing) */
  static resetCinematic(sequenceId: string): void {
    resetCinematicCompletion(sequenceId);
  }

  destroy(): void {
    EventBus.off(CinematicEvents.START, this.onStartRequested, this);
    EventBus.off(CinematicEvents.SKIP, this.skip, this);
    EventBus.off(CinematicEvents.FORCE_STOP, this.forceStop, this);
    this.cleanupAll();
    this.camera.destroy();
    this.bubble.destroy();
    this.cursor.destroy();
    this.recap.destroy();
    this.spotlight.destroy();
    this.demoRunner?.destroy();
  }
}

